import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import useCountdown from 'hooks/countdown';
import PropTypes from 'prop-types';

import { useGetPromoEventQuery } from 'store/api/tournamentService/promoLobbyController';
import { userSelector } from 'store/slices/user';

import { formatDateTime } from 'utils/date';

import Loader from 'components/Loader/Loader';

import { STATUSES, getCategory, getType } from 'components/config/tournaments';

import styles from 'components/TournamentPopup/Banner/Banner.module.scss';

const Banner = ({ event: { eventId, eventStatus, images, schedule } }) => {
  const userInfo = useSelector(userSelector.getInfo);

  const { data, isLoading } = useGetPromoEventQuery(
    {
      eventId,
      userId: userInfo?.id,
    },
    {
      skip: !userInfo,
    },
  );

  const { event } = data ?? {};

  const timeLeft = useCountdown(
    eventStatus === STATUSES.running
      ? new Date(schedule?.activeStatusFinishTime)
      : new Date(schedule?.activeStatusStartTime),
  );

  const categoryConfig = useMemo(
    () => getCategory(getType[eventStatus]),
    [eventStatus],
  );

  if (isLoading) return <Loader />;

  return (
    <div className={styles.wrapper}>
      <img
        src={images?.smallBannerUrl}
        className={styles.img}
        alt="PromoBanner"
      />
      <div className={styles.content}>
        {categoryConfig && (
          <div className={styles.tag}>
            <div
              className={styles.tagContent}
              style={{ color: categoryConfig.color }}
            >
              {categoryConfig.title}
            </div>
          </div>
        )}
        <div className={styles.title}>{event?.eventTitle}</div>
        <div className={styles.period}>
          {formatDateTime(new Date(schedule?.activeStatusStartTime))}
          {' - '}
          {formatDateTime(new Date(schedule?.activeStatusFinishTime))}
        </div>
        <div className={styles.info}>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>
              {eventStatus === STATUSES.running ? 'Ends in' : 'Starts in'}:
            </span>
            <span className={styles.infoValue}>
              {timeLeft
                ? `${timeLeft.days}d : ${timeLeft.hours}h : ${timeLeft.minutes}m : ${timeLeft.seconds}s`
                : '-'}
            </span>
          </div>
          <div className={styles.infoBlock}>
            <span className={styles.infoLabel}>Prize pool:</span>
            <span className={styles.infoValue}>
              {event?.prizePools?.eventPrizePool}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

Banner.propTypes = {
  event: PropTypes.shape({
    eventId: PropTypes.string.isRequired,
    eventStatus: PropTypes.string.isRequired,
    images: PropTypes.shape({
      smallBannerUrl: PropTypes.string.isRequired,
    }).isRequired,
    schedule: PropTypes.shape({
      activeStatusStartTime: PropTypes.string.isRequired,
      activeStatusFinishTime: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default Banner;
